import { useState } from "react";
import { useIntl } from "react-intl";
import {
  createPaymentCartApi,
  processManualCartPaymentApi,
  createCartStripeCheckoutApi,
  CartItem,
} from "../utils/api/paymentCart";
import Notiflix from "notiflix";

export const useMultiPackagePayment = () => {
  const [loading, setLoading] = useState(false);
  const intl = useIntl();

  const handleManualPayment = async (
    attendeeId: number,
    cartItems: CartItem[]
  ) => {
    setLoading(true);
    try {
      // Create payment cart
      const cartData = {
        attendeeId,
        items: cartItems.map((item) => ({
          priceId: item.priceId,
          quantity: item.quantity,
        })),
      };

      const cartResponse = await createPaymentCartApi(cartData);
      const cart = cartResponse.data;

      // Process manual payment
      await processManualCartPaymentApi(cart.id);

      Notiflix.Notify.success(
        intl.formatMessage(
          {
            id: "payment.success.manual",
          },
          {
            classes: cart.items.reduce(
              (total, item) =>
                total + item.price.numberOfClasses * item.quantity,
              0
            ),
          }
        )
      );

      return { success: true };
    } catch (error: any) {
      console.error("Manual cart payment error:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message ||
          intl.formatMessage({
            id: "payment.createError",
          })
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async (
    attendeeId: number,
    cartItems: CartItem[]
  ) => {
    setLoading(true);
    try {
      // Create payment cart
      const cartData = {
        attendeeId,
        items: cartItems.map((item) => ({
          priceId: item.priceId,
          quantity: item.quantity,
        })),
      };

      const cartResponse = await createPaymentCartApi(cartData);
      const cart = cartResponse.data;

      // Create Stripe checkout session
      const checkoutResponse = await createCartStripeCheckoutApi(cart.id);
      const { url } = checkoutResponse.data;

      if (url) {
        // Redirect to Stripe checkout
        window.location.href = url;
        return { success: true };
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      console.error("Stripe cart payment error:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message ||
          intl.formatMessage({
            id: "payment.createError",
          })
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleManualPayment,
    handleStripePayment,
  };
};
